import { useEffect, useState } from 'react'

export type XRFeature =
  | 'hand-tracking'
  | 'plane-detection'
  | 'mesh-detection'
  | 'hit-test'
  | 'anchors'
  | 'depth-sensing'
  | 'layers'
  | 'dom-overlay'

export type SessionMode = 'immersive-ar' | 'immersive-vr' | 'inline'

export interface ModeSupport {
  ar: boolean
  vr: boolean
  inline: boolean
}

export function pickBestMode(s: ModeSupport): SessionMode {
  if (s.ar) return 'immersive-ar'
  if (s.vr) return 'immersive-vr'
  return 'inline'
}

const COMMON_OPTIONAL = ['bounded-floor', 'hand-tracking', 'layers']
const MR_OPTIONAL = ['plane-detection', 'mesh-detection', 'hit-test', 'anchors']

export function desiredFeatures(mode: SessionMode): { required: string[]; optional: string[] } {
  if (mode === 'inline') return { required: [], optional: [] }
  return {
    required: ['local-floor'],
    optional: mode === 'immersive-ar' ? [...COMMON_OPTIONAL, ...MR_OPTIONAL] : COMMON_OPTIONAL,
  }
}

const ALL_FEATURES: XRFeature[] = [
  'hand-tracking',
  'plane-detection',
  'mesh-detection',
  'hit-test',
  'anchors',
  'depth-sensing',
  'layers',
  'dom-overlay',
]

export function gateFeatures(enabled: readonly string[]): Record<XRFeature, boolean> {
  const out = {} as Record<XRFeature, boolean>
  for (const f of ALL_FEATURES) out[f] = enabled.includes(f)
  return out
}

export function useModeSupport(retrigger?: unknown): ModeSupport {
  const [support, setSupport] = useState<ModeSupport>({ ar: false, vr: false, inline: true })
  useEffect(() => {
    const xr = navigator.xr
    if (!xr) return
    let cancelled = false
    Promise.all([
      xr.isSessionSupported('immersive-ar').catch(() => false),
      xr.isSessionSupported('immersive-vr').catch(() => false),
    ]).then(([ar, vr]) => {
      if (!cancelled) setSupport({ ar, vr, inline: true })
    })
    return () => {
      cancelled = true
    }
  }, [retrigger])
  return support
}
